import { notifications } from '@mantine/notifications';
import * as Sentry from '@sentry/react';
import { normalizeApiError } from '@/common/errors/api-error.ts';

export type ErrorContext = {
  route?: string;
  operation?: string;
  queryKey?: string;
  mutationKey?: string;
};

export function captureWithContext(
  error: unknown,
  context: ErrorContext = {},
): string | undefined {
  const normalized = normalizeApiError(error);
  if (normalized.kind === 'abort') return undefined;

  return Sentry.withScope((scope) => {
    if (context.route) scope.setTag('route', context.route);
    if (context.operation) scope.setTag('operation', context.operation);
    if (context.queryKey) scope.setTag('query_key', context.queryKey);
    if (context.mutationKey) scope.setTag('mutation_key', context.mutationKey);
    if (normalized.kind) scope.setTag('error_kind', normalized.kind);
    if (normalized.status) scope.setTag('http_status', String(normalized.status));
    if (normalized.method) scope.setTag('http_method', normalized.method);
    if (normalized.path) scope.setTag('http_path', normalized.path);
    if (normalized.requestId) scope.setTag('request_id', normalized.requestId);
    scope.setContext('api_error', {
      kind: normalized.kind,
      status: normalized.status,
      method: normalized.method,
      path: normalized.path,
      requestId: normalized.requestId,
    });
    return Sentry.captureException(normalized);
  });
}

export function openLinkedFeedback({
  eventId,
  title,
}: {
  eventId?: string;
  title?: string;
}) {
  if (!eventId) {
    notifications.show({
      title: 'Reporting unavailable',
      message: 'No linked error event found. Please contact support.',
      color: 'yellow',
    });
    return;
  }

  try {
    Sentry.showReportDialog({ eventId, title: title ?? 'Report an issue' });
  } catch {
    notifications.show({
      title: 'Reporting unavailable',
      message: `Please contact support and share event ID: ${eventId}`,
      color: 'yellow',
    });
  }
}

export function setSentryUserContext(
  user?: { id?: string; name?: string; email?: string; role?: string | null } | null,
) {
  if (!user) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: user.id,
    name: user.name,
    email: user.email,
  });

  const role = user.role;
  if (typeof role === 'string' && role.length > 0) {
    Sentry.setTag('user_role', role);
  }
}

export function addAppBreadcrumb(params: {
  category: string;
  message: string;
  data?: Record<string, unknown>;
  level?: Sentry.SeverityLevel;
}) {
  Sentry.addBreadcrumb({
    category: params.category,
    message: params.message,
    data: params.data,
    level: params.level ?? 'info',
  });
}
